import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { platform, type BillingSettingsDto, type CountryDto } from '../lib/api.js'
import { Button, Card, Input, Spinner, Switch, toast } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

/**
 * Plataforma → Países e trial (#293).
 *
 * - Países: `enabled_signup` (cadastro self-serve do bundle) e
 *   `homologated_full` (versão completa: Small/Pro, pedidos, POS, caixa).
 *   No lançamento: cadastro aberto em todos, só PT homologado.
 * - Config de billing: duração do trial, carência de past_due e regras de
 *   trial único — nada disso é hardcoded.
 */
export function PlatformCountries() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [onlyActive, setOnlyActive] = useState(false)

  const { data: countries = [], isLoading } = useQuery({
    queryKey: ['platform-countries'],
    queryFn: platform.listCountries,
  })
  const update = useMutation({
    mutationFn: (v: {
      code: string
      patch: Partial<Pick<CountryDto, 'enabled_signup' | 'homologated_full'>>
    }) => platform.updateCountry(v.code, v.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-countries'] }),
    onError: () => toast.error(t('admin.countries.save_error')),
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return countries.filter(
      (c) =>
        (!q || c.code.toLowerCase() === q || c.name.toLowerCase().includes(q)) &&
        (!onlyActive || c.homologated_full || !c.enabled_signup),
    )
  }, [countries, search, onlyActive])

  const homologated = countries.filter((c) => c.homologated_full).map((c) => c.code)
  const closed = countries.filter((c) => !c.enabled_signup).length

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <BillingSettingsCard />
      <Card title={t('admin.countries.title')} description={t('admin.countries.desc')}>
        <p className="text-sm text-gray-600 mb-3" data-testid="countries-summary">
          {t('admin.countries.summary', {
            homologated: homologated.join(', ') || '—',
            closed: String(closed),
          })}
        </p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex-1 min-w-[12rem]">
            <Input
              label={t('admin.countries.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="countries-search"
            />
          </div>
          <Switch
            checked={onlyActive}
            onChange={setOnlyActive}
            label={t('admin.countries.only_exceptions')}
          />
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="w-5 h-5 text-indigo-400" />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-gray-500">
                <th className="py-2 pr-3 font-medium">{t('admin.countries.country')}</th>
                <th className="py-2 pr-3 font-medium">{t('admin.countries.signup')}</th>
                <th className="py-2 font-medium">{t('admin.countries.homologated')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((c) => (
                <tr key={c.code} data-testid={`country-${c.code}`}>
                  <td className="py-2 pr-3">
                    <span className="font-mono text-gray-500 mr-2">{c.code}</span>
                    {c.name}
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      aria-label={`${c.code} ${t('admin.countries.signup')}`}
                      data-testid={`country-${c.code}-signup`}
                      checked={c.enabled_signup}
                      disabled={update.isPending}
                      onChange={(e) =>
                        update.mutate({ code: c.code, patch: { enabled_signup: e.target.checked } })
                      }
                    />
                  </td>
                  <td className="py-2">
                    <input
                      type="checkbox"
                      aria-label={`${c.code} ${t('admin.countries.homologated')}`}
                      data-testid={`country-${c.code}-homologated`}
                      checked={c.homologated_full}
                      disabled={update.isPending}
                      onChange={(e) =>
                        update.mutate({
                          code: c.code,
                          patch: { homologated_full: e.target.checked },
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}

function BillingSettingsCard() {
  const { t } = useI18n()
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['billing-settings'], queryFn: platform.billingSettings })
  const [draft, setDraft] = useState<Partial<BillingSettingsDto>>({})
  const save = useMutation({
    mutationFn: () => platform.updateBillingSettings(draft),
    onSuccess: () => {
      setDraft({})
      toast.success(t('admin.billing_cfg.saved'))
      return qc.invalidateQueries({ queryKey: ['billing-settings'] })
    },
    onError: () => toast.error(t('admin.billing_cfg.save_error')),
  })
  if (!data) return null
  const v = { ...data, ...draft }
  const num = (k: 'trial_days' | 'past_due_grace_days') => (
    <Input
      type="number"
      min={0}
      label={t(`admin.billing_cfg.${k}`)}
      value={String(v[k])}
      data-testid={`billing-${k}`}
      onChange={(e) => setDraft({ ...draft, [k]: Math.max(0, Number(e.target.value) || 0) })}
    />
  )
  const flag = (
    k: 'trial_once_per_tenant' | 'trial_once_per_owner_email' | 'existing_tenants_new_trial',
  ) => (
    <Switch
      checked={v[k]}
      onChange={(on) => setDraft({ ...draft, [k]: on })}
      label={t(`admin.billing_cfg.${k}`)}
      testId={`billing-${k}`}
    />
  )
  return (
    <Card title={t('admin.billing_cfg.title')} description={t('admin.billing_cfg.desc')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {num('trial_days')}
        {num('past_due_grace_days')}
      </div>
      <div className="space-y-3 mb-4">
        {flag('trial_once_per_tenant')}
        {flag('trial_once_per_owner_email')}
        {flag('existing_tenants_new_trial')}
      </div>
      <Button
        size="sm"
        disabled={Object.keys(draft).length === 0}
        loading={save.isPending}
        onClick={() => save.mutate()}
        data-testid="billing-settings-save"
      >
        {t('admin.billing_cfg.save')}
      </Button>
    </Card>
  )
}
